import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, interval, takeUntil } from 'rxjs';
import { initBoomStickDataset, t_BoomStickDataset } from '../../data/boomStick-datanames';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { t_DataPoint } from '../../../../../services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { dataIdentifierEqual } from '../../../../../services/helper/did-handling-helper';

@Component({
    selector: 'app-boom-stick-position',
    templateUrl: './boom-stick-position.component.html',
    styleUrls: ['./boom-stick-position.component.scss'],
})
export class BoomStickPositionComponent implements OnInit, OnDestroy {
    public boomStickDataset: t_BoomStickDataset = JSON.parse(JSON.stringify(initBoomStickDataset));
    public queryFrequency: number = 400;
    public loadedAndQueryStarting: boolean = false;
    public imgLoaded: boolean = false;
    public pollingFrequency: number;
    private destroy$: Subject<void> = new Subject<void>();
    private destroyed: boolean = false;

    constructor(
        public gatewayConnectionService: GatewayConnectionService,
        private didConfigService: DidConfigService,
        private dataRetrieval: DidDataRetrievalService
    ) {
        if (this.queryFrequency <= 65) {
            this.pollingFrequency = 50;
        } else {
            this.pollingFrequency = parseFloat((this.queryFrequency * 0.75).toFixed(0));
        }
    }

    ngOnInit(): void {
        this.setupServices().then(() => {
            if(!this.destroyed) {
                this.dataRetrieval.initService();
                this.getDataIdentifiersToDataNamesFromConfig();
                this.postDataIdentifierToQueryToBackend().then(() => {
                    if(this.destroyed){
                        this.ngOnDestroy();
                    }
                    this.dataRetrieval.startDataQuery();
                    this.loadedAndQueryStarting = true;
                    this.startPeriodicPolling();
                });
            // Init Data Query List
            // postDataQueryListToBackend
            }
        });
    }
    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.dataRetrieval.stopSendQueryAliveState();
        this.dataRetrieval.stopDataQuery();
        this.dataRetrieval.cleanDataPoints();
        this.gatewayConnectionService.checkGatewayAlive().subscribe({
            next: (isAlive) => {
                if(isAlive) {
                    this.boomStickDataset.dataPoints.forEach(data => {
                        this.didConfigService.removeDidFromQueryList({ecu_id: data.dataName.identifier!.ecu_id, data_id: data.dataName.identifier!.data_id}).subscribe();
                    });
                }
            }
        });        
    }

    private setupServices(): Promise<void> {
        return new Promise((resolve) => {
            this.gatewayConnectionService.establishConnectionToGateway().then(
                () => {
                    resolve();
                    // Resolve, Connection established, Load DidMapping
                    /*this.didConfigService.loadEcuList().then(() => {
                        this.didConfigService.ecuList!.forEach((ecu) => {
                            this.didConfigService.setEcuDidMapping(ecu.ecu_id).then(() => {
                                resolve();
                            });
                        });
                    });*/
                },
                () => {
                    // Rejected, no connection possible
                    console.error('ERROR');
                }
            );
        });
    }

    public getDataIdentifiersToDataNamesFromConfig() {
        this.boomStickDataset.dataPoints.forEach((boomStickDataPoint) => {
            boomStickDataPoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(boomStickDataPoint.dataName.name);
            boomStickDataPoint.dataName.didInformation = this.didConfigService.getDidInformation(boomStickDataPoint.dataName.identifier);
        });
        this.boomStickDataset.dataPoints = this.boomStickDataset.dataPoints
            .map((dataPoint) => dataPoint)
            .filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined);
        if(this.destroyed) {
            this.ngOnDestroy();
        }
    }

    private postDataIdentifierToQueryToBackend(): Promise<void> {
        return new Promise((resolve) => {
            let dataIdentifierList: t_DataIdentifier[] = this.boomStickDataset.dataPoints
                .map((dataPoint) => dataPoint.dataName.identifier)
                .filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
            this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
                complete: () => {
                    resolve();
                },
            });
        });
    }

    private startPeriodicPolling() {
        interval(this.pollingFrequency)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.getDataFromBackend();
            });
    }

    private getDataFromBackend() {
        // Get all Dids Values, because did list was Posted, so no other dids are present in the list
        this.dataRetrieval.getAllDidsValues().subscribe((response) => {
            if (response.body != null) {
                this.processRawData(response.body);
            }
        });
    }

    private processRawData(rawData: t_DataPoint[]) {
        rawData.forEach((dataPoint) => {
            if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
                return;
            }
            let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);

            let normalizedValue: number = this.valueToNormalizedUnit(
                dataPoint.data.value[0],
                this.boomStickDataset.dataPoints[indexInDataset].dataName.didInformation!.unit,
                this.boomStickDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
                this.boomStickDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
            );
            this.boomStickDataset.dataPoints[indexInDataset].value = normalizedValue;
        });
    }

    private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
        let index: number = this.boomStickDataset.dataPoints.findIndex((datapoint) => {
            if (datapoint.dataName.identifier) {
                return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
            } else {
                return false;
            }
        });
        return index;
    }

    private valueToNormalizedUnit(value: number, unit: string, factor: number, offset: number): number {
        if(factor == 0){
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        if (unit == 'Unknown') {
            res = Number.NaN; 
        }
        return res;
    }
}
